import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import fleetApi from "@/lib/fleetApi";
import { Panel, StatusPill, RiskBadge, Loading } from "@/components/portal/widgets";
import { useLiveRefresh, UpdatedAgo } from "@/lib/liveRefresh";
import VehicleAssignDialog from "@/components/portal/VehicleAssignDialog";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Phone, IdCard, Star, Clock, ShieldAlert, Route } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function Stat({ icon: Icon, label, value, accent = "slate" }) {
  const c = { emerald: "text-emerald-400", amber: "text-amber-400", blue: "text-blue-400", slate: "text-slate-200", red: "text-red-400" }[accent];
  return (
    <div className="rounded-xl border border-slate-800 bg-[#111827] p-5">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Icon className="h-4 w-4" />{label}</div>
      <div className={`mt-2 font-mono-data text-2xl font-bold ${c}`}>{value}</div>
    </div>
  );
}

export default function DriverDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [stats, setStats] = useState(null);
  const [trips, setTrips] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [error, setError] = useState(false);
  const { user } = useAuth();
  const canAssignVehicle = ["owner", "administrator"].includes(user?.role);
  const [myVehicle, setMyVehicle] = useState(null);
  const [vehOpen, setVehOpen] = useState(false);

  const load = useCallback(() => Promise.all([
    fleetApi.get(`/drivers/${id}`),
    fleetApi.get(`/drivers/${id}/stats`).catch(() => ({ data: null })),
    fleetApi.get(`/trips?driver_id=${id}&limit=200`).catch(() => ({ data: [] })),
    fleetApi.get(`/incidents`).catch(() => ({ data: [] })),
    fleetApi.get(`/vehicles`).catch(() => ({ data: [] })),
  ]).then(([d, s, t, inc, veh]) => {
    setMyVehicle((veh.data || []).find((v) => v.current_driver_id === id) || null);
    setDriver(d.data); setStats(s.data);
    setTrips(Array.isArray(t.data) ? t.data : t.data?.trips || []);
    const list = Array.isArray(inc.data) ? inc.data : inc.data?.incidents || [];
    setIncidents(list.filter((x) => x.driver_id === id));
  }), [id]);

  useEffect(() => { load().catch(() => setError(true)); }, [load]);
  const updatedAt = useLiveRefresh(load, 20000);

  if (error) return <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-5 py-8 text-center text-sm text-red-300" data-testid="driver-detail-error">Couldn't load this driver.</div>;
  if (!driver) return <Loading />;

  const m = driver.metrics || {};
  const periods = stats?.periods || {};
  const template = driver.shift_template || {};

  return (
    <div data-testid="driver-detail-page">
      <div className="mb-2 flex justify-end"><UpdatedAgo at={updatedAt} testid="driver-live-updated" /></div>
      <button onClick={() => navigate("/portal/fleet")} data-testid="driver-back"
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 transition-colors hover:text-slate-200">
        <ArrowLeft className="h-4 w-4" /> Back to Drivers & Fleet
      </button>

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-600 text-xl font-bold text-white">
          {(driver.full_name || driver.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}
        </span>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold text-white" data-testid="driver-name">{driver.full_name || driver.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-slate-400">
            <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{driver.phone || "—"}</span>
            <span className="flex items-center gap-1.5"><IdCard className="h-3.5 w-3.5" />{driver.license_number || "—"} · exp {driver.license_expiration || "—"}</span>
          </div>
        </div>
        <StatusPill value={driver.available ? driver.status : "off_duty"} />
        {!driver.eligible && <StatusPill value="ineligible" />}
        <span className="text-sm text-slate-300" data-testid="driver-vehicle">Vehicle: <b className="text-white">{myVehicle ? `${myVehicle.fleet_number} · ${myVehicle.make || ""} ${myVehicle.model || ""}` : "none"}</b></span>
        {canAssignVehicle && (
          <button onClick={() => setVehOpen(true)} data-testid="driver-assign-vehicle-btn"
            className="rounded-md bg-blue-700 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-800">
            {myVehicle ? "Switch vehicle" : "Assign vehicle"}
          </button>
        )}
      </div>
      {canAssignVehicle && <VehicleAssignDialog driverId={id} open={vehOpen} onOpenChange={setVehOpen} onSaved={load} />}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Star} label="On-time rate" value={`${m.on_time_rate ?? "—"}%`} accent="emerald" />
        <Stat icon={Route} label="Trips (lifetime)" value={(m.trips_completed ?? 0).toLocaleString()} accent="blue" />
        <Stat icon={Clock} label="Late pickups" value={m.late_pickups ?? "—"} accent="amber" />
        <Stat icon={ShieldAlert} label="Incidents" value={m.incidents ?? incidents.length} accent="red" />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_1fr]">
        <Panel title="On-Time Trend" testid="driver-trend">
          <div className="p-5">
            <table className="w-full text-sm">
              <thead><tr className="text-left font-mono-data text-[11px] uppercase tracking-wider text-slate-500">
                <th className="pb-2">Period</th><th className="pb-2">Completed</th><th className="pb-2">Late</th><th className="pb-2">Missed</th><th className="pb-2">On-time</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-800">
                {["week", "month", "lifetime"].map((p) => {
                  const d = periods[p] || {};
                  const comp = d.completed || 0; const late = d.late || 0;
                  const rate = comp ? Math.round(((comp - late) / comp) * 100) : 0;
                  return (
                    <tr key={p} data-testid={`driver-period-${p}`}>
                      <td className="py-2.5 font-semibold capitalize text-white">{p}</td>
                      <td className="py-2.5 font-mono-data text-slate-300">{comp}</td>
                      <td className="py-2.5 font-mono-data text-amber-400">{late}</td>
                      <td className="py-2.5 font-mono-data text-red-400">{d.missed || 0}</td>
                      <td className="py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${rate}%` }} /></div>
                          <span className="font-mono-data text-xs text-slate-400">{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Weekly Schedule" testid="driver-schedule">
          <div className="divide-y divide-slate-800">
            {DAYS.map((day, i) => {
              const s = template[String(i)];
              return (
                <div key={day} className="flex items-center justify-between px-5 py-2.5 text-sm">
                  <span className="font-semibold text-slate-300">{day}</span>
                  <span className="font-mono-data text-slate-400">{s ? `${s.start} – ${s.end}` : <span className="text-slate-600">Off</span>}</span>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <Panel title="Recent Trips" testid="driver-trips"
          action={<span className="font-mono-data text-xs text-slate-500">{trips.length}</span>}>
          <div className="max-h-[400px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#111827]"><tr className="border-b border-slate-800 text-left font-mono-data text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Trip / Rider</th><th className="px-3 py-3">Status</th><th className="px-3 py-3">Risk</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-800">
                {trips.length === 0 && <tr><td colSpan={3} className="px-5 py-8 text-center text-slate-500">No trips found for this driver.</td></tr>}
                {trips.slice(0, 100).map((t) => (
                  <tr key={t.id} onClick={() => navigate(`/portal/trips/${t.id}`)} className="cursor-pointer hover:bg-slate-800/30" data-testid={`driver-trip-${t.id}`}>
                    <td className="px-5 py-2.5"><div className="font-semibold text-white">{t.rider_name}</div><div className="font-mono-data text-[11px] text-slate-500">{t.external_trip_id}</div></td>
                    <td className="px-3 py-2.5"><StatusPill value={t.status} /></td>
                    <td className="px-3 py-2.5"><RiskBadge trip={t} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Incidents" testid="driver-incidents"
          action={<span className="font-mono-data text-xs text-slate-500">{incidents.length}</span>}>
          <div className="max-h-[400px] divide-y divide-slate-800 overflow-auto">
            {incidents.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500">No incidents on record.</div>}
            {incidents.map((i) => (
              <div key={i.id} className="px-5 py-3" data-testid={`driver-incident-${i.id}`}>
                <div className="flex items-center justify-between"><span className="font-semibold text-white">{i.type}</span><StatusPill value={i.status} /></div>
                <div className="mt-1 line-clamp-2 text-xs text-slate-500">{i.description}</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
